import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLandscapeOverlay } from '@/hooks/useLandscapeOverlay';

const DEADPAN_MESSAGES = [
  "Your phone is sideways.",
  "Interesting choice.",
  "We don't do landscape here.",
  "The colors prefer portrait.",
  "Rotate your device. Or don't.",
  "This is not a movie.",
  "The eye sees vertically.",
  "Sideways vibes detected.",
  "Portrait mode awaits.",
  "The feed flows downward.",
];

const ROTATE_MESSAGES = [
  "Almost there.",
  "The colors are waiting.",
  "Portrait, please.",
  "Tilt to finish.",
  "One rotation away.",
  "The moment needs you vertical.",
  "Flip it back.",
  "Your colors miss portrait.",
  "Rotate to seal the vibe.",
  "Just a little twist.",
];

const RAINBOW_COLORS = [
  '#f87171', // rose
  '#fb923c', // orange
  '#fbbf24', // amber
  '#a3e635', // lime
  '#34d399', // emerald
  '#22d3ee', // cyan
  '#60a5fa', // blue
  '#a78bfa', // violet
  '#f472b6', // pink
];

const BAND_COLORS = [
  { from: 'rgb(252, 165, 165)', to: 'rgb(248, 113, 113)' }, // rose
  { from: 'rgb(252, 211, 77)', to: 'rgb(251, 191, 36)' },   // amber
  { from: 'rgb(110, 231, 183)', to: 'rgb(52, 211, 153)' },  // emerald
  { from: 'rgb(125, 211, 252)', to: 'rgb(56, 189, 248)' },  // sky
  { from: 'rgb(196, 181, 253)', to: 'rgb(167, 139, 250)' }, // violet
];

type ShutterPhase = 'idle' | 'ready' | 'closing' | 'closed' | 'opening';

export function LandscapeOverlay() {
  const [isLandscape, setIsLandscape] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [message, setMessage] = useState(() =>
    DEADPAN_MESSAGES[Math.floor(Math.random() * DEADPAN_MESSAGES.length)]
  );
  const [rotateMessage, setRotateMessage] = useState(() => 
    ROTATE_MESSAGES[Math.floor(Math.random() * ROTATE_MESSAGES.length)]
  );
  const [shutterPhase, setShutterPhase] = useState<ShutterPhase>('idle');
  const [rotatePromptOpacity, setRotatePromptOpacity] = useState(0);
  const wasLandscape = useRef(false);
  const shutterTimeoutRef = useRef<number | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { mode } = useLandscapeOverlay();

  const checkOrientation = useCallback(() => {
    const landscape = window.innerWidth > window.innerHeight;
    
    // Pick a new random message only when transitioning from portrait to landscape
    if (landscape && !wasLandscape.current) {
      setMessage(DEADPAN_MESSAGES[Math.floor(Math.random() * DEADPAN_MESSAGES.length)]);
    }
    wasLandscape.current = landscape;
    
    setIsLandscape(landscape);
    
    // Consider mobile if touch device AND small screen
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isSmallScreen = Math.min(window.innerWidth, window.innerHeight) < 500;
    setIsMobile(isTouchDevice && isSmallScreen);
  }, []);

  useEffect(() => {
    checkOrientation();
    
    // Use both resize and orientationchange for better iOS Safari support
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);
    
    // Also check on a short interval for iOS Safari edge cases
    const interval = setInterval(checkOrientation, 500);
    
    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
      clearInterval(interval);
    };
  }, [checkOrientation]);

  // Cleanup shutter timeouts
  useEffect(() => {
    return () => {
      if (shutterTimeoutRef.current) {
        clearTimeout(shutterTimeoutRef.current);
      }
    };
  }, []);

  // Fade in rotate prompt when mode changes to 'rotate'
  useEffect(() => {
    if (mode === 'rotate') {
      // Pick new rotate message
      setRotateMessage(ROTATE_MESSAGES[Math.floor(Math.random() * ROTATE_MESSAGES.length)]);
      // Start fade in
      const timer = setTimeout(() => setRotatePromptOpacity(1), 50);
      return () => clearTimeout(timer);
    } else if (mode === 'rotate-instant') {
      // Pick new rotate message
      setRotateMessage(ROTATE_MESSAGES[Math.floor(Math.random() * ROTATE_MESSAGES.length)]);
      // Instant show - no fade
      setRotatePromptOpacity(1);
    } else {
      setRotatePromptOpacity(0);
    }
  }, [mode]);

  const handleOpenCamera = () => {
    // Start shutter animation
    setShutterPhase('ready');
    
    shutterTimeoutRef.current = window.setTimeout(() => {
      setShutterPhase('closing');
      
      shutterTimeoutRef.current = window.setTimeout(() => {
        setShutterPhase('closed');
        
        // Navigate/dispatch while shutter is closed
        if (location.pathname === '/') {
          window.dispatchEvent(new CustomEvent('openCamera'));
        } else {
          navigate('/?camera=1');
        }
        
        // Keep shutter closed a bit longer, then it will be hidden by camera's own shutter
        shutterTimeoutRef.current = window.setTimeout(() => {
          setShutterPhase('idle');
        }, 600);
      }, 200);
    }, 20);
  };

  // Don't show on desktop, when suppressed, or in portrait mode
  const isRotateMode = mode === 'rotate' || mode === 'rotate-instant';
  const shouldShow = isLandscape && mode !== 'suppressed' && isMobile;

  // Disable scroll when overlay is shown
  useEffect(() => {
    if (shouldShow) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [shouldShow]);

  // Generate wavy SVG path points
  const generateWavyPoints = (y: number, amplitude: number, frequency: number, phase: number) => {
    const points: string[] = [];
    const width = 2000;
    for (let x = 0; x <= width; x += 8) {
      const waveY = y + Math.sin((x * frequency) + phase) * amplitude + Math.sin((x * frequency * 2.3) + phase * 1.7) * (amplitude * 0.4);
      points.push(`${x},${waveY}`);
    }
    return points;
  };

  // Shutter overlay - horizontal bars for landscape mode (left/right)
  const shutterOverlay = shutterPhase !== 'idle' ? (
    <div className="fixed inset-0 z-[110] pointer-events-none overflow-hidden">
      {/* Left bar */}
      <div 
        className="absolute top-0 bottom-0 left-0 bg-gradient-to-r from-zinc-700 to-zinc-800 overflow-hidden"
        style={{
          width: 'calc(50vw + 2px)',
          transform: shutterPhase === 'closing' || shutterPhase === 'closed' 
            ? 'translateX(0)' 
            : 'translateX(-100%)',
          transition: shutterPhase === 'ready' 
            ? 'none' 
            : 'transform 200ms cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <div className="absolute inset-0 opacity-20 flex flex-row justify-evenly px-8">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="w-px bg-zinc-400 h-full" />
          ))}
        </div>
        <div className="absolute top-0 bottom-0 right-0 w-1 bg-gradient-to-r from-zinc-600 to-zinc-900" />
      </div>
      
      {/* Right bar */}
      <div 
        className="absolute top-0 bottom-0 right-0 bg-gradient-to-l from-zinc-700 to-zinc-800 overflow-hidden"
        style={{
          width: 'calc(50vw + 2px)',
          transform: shutterPhase === 'closing' || shutterPhase === 'closed' 
            ? 'translateX(0)' 
            : 'translateX(100%)',
          transition: shutterPhase === 'ready' 
            ? 'none' 
            : 'transform 200ms cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <div className="absolute inset-0 opacity-20 flex flex-row justify-evenly px-8">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="w-px bg-zinc-400 h-full" />
          ))}
        </div>
        <div className="absolute top-0 bottom-0 left-0 w-1 bg-gradient-to-l from-zinc-600 to-zinc-900" />
      </div>

      {/* Center eye when closed */}
      <div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transition-all duration-200"
        style={{
          opacity: shutterPhase === 'closed' ? 1 : 0,
          transform: `translate(-50%, -50%) scale(${shutterPhase === 'closed' ? 1 : 0.5})`,
        }}
      >
        <div className="relative w-20 h-20">
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-rose-300 via-amber-200 to-sky-300 animate-pulse" />
          <div className="absolute inset-2 rounded-full bg-white/80 dark:bg-black/30" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-4 h-4 rounded-full bg-zinc-800" />
          </div>
        </div>
      </div>
    </div>
  ) : null;

  // Show shutter even when overlay is suppressed (during camera transition)
  if (!shouldShow) {
    return shutterOverlay;
  }

  // Rotate prompt mode - shown after leaving camera in landscape
  if (isRotateMode) {
    // Skip transition for instant mode
    const isInstant = mode === 'rotate-instant';
    
    return (
      <div 
        className={`fixed inset-0 z-[100] flex items-center justify-center overflow-hidden ${isInstant ? '' : 'transition-opacity duration-300'}`}
        style={{ opacity: rotatePromptOpacity }}
      >
        {/* Dark gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-violet-950 to-slate-900" />
        
        {/* Rainbow rain - heavier and faster */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Layer 1 - main drops */}
          {RAINBOW_COLORS.map((color, i) => (
            <div
              key={i}
              className="absolute animate-rainbow-fall-fast"
              style={{
                left: `${(i * 11) + 3}%`,
                top: '-15%',
                width: '3px',
                height: '12%',
                background: `linear-gradient(to bottom, transparent, ${color}88, ${color}, ${color}88, transparent)`,
                borderRadius: '2px',
                animationDelay: `${i * 0.15}s`,
              }}
            />
          ))}
          {/* Layer 2 - offset drops */}
          {RAINBOW_COLORS.map((color, i) => (
            <div
              key={`b-${i}`}
              className="absolute animate-rainbow-fall-fast"
              style={{
                left: `${(i * 11) + 8}%`,
                top: '-15%',
                width: '2px',
                height: '10%',
                background: `linear-gradient(to bottom, transparent, ${color}66, ${color}cc, ${color}66, transparent)`,
                borderRadius: '2px',
                animationDelay: `${i * 0.15 + 0.6}s`,
              }}
            />
          ))}
          {/* Layer 3 - more drops for density */}
          {RAINBOW_COLORS.map((color, i) => (
            <div
              key={`c-${i}`}
              className="absolute animate-rainbow-fall-fast"
              style={{
                left: `${(i * 11) + 1}%`,
                top: '-15%',
                width: '2px',
                height: '8%',
                background: `linear-gradient(to bottom, transparent, ${color}44, ${color}aa, ${color}44, transparent)`,
                borderRadius: '2px',
                animationDelay: `${i * 0.15 + 1.2}s`,
              }}
            />
          ))}
        </div>
        
        {/* Subtle color accents */}
        <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-rose-500/10 blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full bg-amber-500/10 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-violet-500/10 blur-3xl" />
        
        {/* Content with umbrella shield */}
        <div className="relative z-10 text-center px-6">
          {/* Umbrella above content */}
          <div className="text-6xl mb-2 select-none">☂️</div>
          
          {/* Rotating phone icon */}
          <div className="relative w-28 h-28 mx-auto animate-rotate-phone">
            <svg viewBox="0 0 100 100" className="w-full h-full">
              {/* Phone outline */}
              <rect 
                x="30" y="15" width="40" height="70" rx="6" 
                fill="none" 
                stroke="url(#arrow-gradient)" 
                strokeWidth="3"
              />
              {/* Screen */}
              <rect 
                x="34" y="22" width="32" height="56" rx="2" 
                fill="rgba(255,255,255,0.1)"
              />
              {/* Rotation arrow */}
              <path 
                d="M 75 50 A 25 25 0 1 1 50 25" 
                fill="none" 
                stroke="url(#arrow-gradient)" 
                strokeWidth="3" 
                strokeLinecap="round"
              />
              <path 
                d="M 50 25 L 56 32 M 50 25 L 44 32" 
                fill="none" 
                stroke="url(#arrow-gradient)" 
                strokeWidth="3" 
                strokeLinecap="round"
              />
              <defs>
                <linearGradient id="arrow-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#f472b6" />
                  <stop offset="50%" stopColor="#fbbf24" />
                  <stop offset="100%" stopColor="#34d399" />
                </linearGradient>
              </defs>
            </svg>
          </div>
          
          {/* Message */}
          <p className="text-lg font-medium text-white/80 max-w-xs mx-auto mt-4">
            {rotateMessage}
          </p>
        </div>
        
        {/* CSS for animations */}
        <style>{`
          @keyframes rotate-phone {
            0%, 100% { transform: rotate(0deg); }
            25% { transform: rotate(-15deg); }
            75% { transform: rotate(15deg); }
          }
          .animate-rotate-phone {
            animation: rotate-phone 2s ease-in-out infinite;
          }
          @keyframes rainbow-fall-fast {
            0% { transform: translateY(0); opacity: 0; }
            5% { opacity: 1; }
            95% { opacity: 1; }
            100% { transform: translateY(130vh); opacity: 0; }
          }
          .animate-rainbow-fall-fast {
            animation: rainbow-fall-fast 1.8s linear infinite;
          }
        `}</style>
      </div>
    );
  }

  // Normal mode - show the full overlay with message and camera button
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden">
      {/* Shutter animation overlay */}
      {shutterOverlay}
      
      {/* Soft warm gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-rose-100 via-amber-50 via-sky-100 to-violet-100 dark:from-rose-950/90 dark:via-amber-950/80 dark:via-sky-950/90 dark:to-violet-950/90" />
      
      {/* Horizontal color bands with wavy borders */}
      <svg className="absolute inset-0 w-full h-full opacity-40" preserveAspectRatio="none" viewBox="0 0 2000 1000">
        <defs>
          {BAND_COLORS.map((color, i) => (
            <linearGradient key={i} id={`band-gradient-${i}`} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={color.from} />
              <stop offset="100%" stopColor={color.to} />
            </linearGradient>
          ))}
        </defs>
        
        {BAND_COLORS.map((_, i) => {
          const bandHeight = 1000 / BAND_COLORS.length;
          const overlap = 40;
          const topY = i * bandHeight - (i === 0 ? 60 : overlap);
          const bottomY = (i + 1) * bandHeight + (i === BAND_COLORS.length - 1 ? 60 : overlap);
          
          const topPoints = generateWavyPoints(topY, 15, 0.012, i * 1.8);
          const bottomPoints = generateWavyPoints(bottomY, 15, 0.015, i * 2.1 + 1);
          
          const pathD = `
            M 0,${topY}
            L ${topPoints.join(' L ')}
            L 2000,${bottomY}
            L ${bottomPoints.reverse().join(' L ')}
            Z
          `;
          
          return (
            <path
              key={i}
              d={pathD}
              fill={`url(#band-gradient-${i})`}
              className="animate-wave-wiggle"
              style={{ 
                animationDelay: `${i * 0.15}s`,
                transformOrigin: 'center',
              }}
            />
          );
        })}
      </svg>
      
      {/* CSS for wave animation */}
      <style>{`
        @keyframes wave-wiggle {
          0%, 100% { transform: translateY(0px) scaleY(1); }
          25% { transform: translateY(6px) scaleY(1.02); }
          50% { transform: translateY(-4px) scaleY(0.98); }
          75% { transform: translateY(5px) scaleY(1.01); }
        }
        .animate-wave-wiggle {
          animation: wave-wiggle 4s ease-in-out infinite;
        }
      `}</style>

      {/* Content */}
      <div className="relative z-10 text-center space-y-8 px-6">
        {/* Sideways eye */}
        <div 
          className="relative w-24 h-24 mx-auto"
          style={{ transform: 'rotate(90deg)' }}
        >
          <span className="text-7xl drop-shadow-lg select-none">👁️</span>
        </div>

        {/* Message */}
        <p className="text-xl font-medium text-foreground/80 max-w-xs mx-auto">
          {message}
        </p>

        {/* Camera button */}
        <button
          onClick={handleOpenCamera}
          disabled={shutterPhase !== 'idle'}
          className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-rose-400 via-amber-400 to-emerald-400 text-white font-medium text-lg shadow-lg shadow-amber-300/30 hover:scale-105 active:scale-95 transition-all disabled:opacity-70"
        >
          <span className="text-xl">📷</span>
          <span>See something?</span>
        </button>
      </div>
    </div>
  );
}
